import React from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/shadcn';
import { LoadingSpinner } from './Loading';
import CommonModal from './CommonModal';

export interface ExportConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isExporting: boolean;
  /** Modal title (default: "Export to Excel") */
  title?: string;
  /** Message shown before export (e.g. "Only the latest 1000 records will be exported. Do you want to continue?") */
  message?: string;
  /** Confirm button label (default: "Export") */
  confirmLabel?: string;
  /** Label shown while exporting (default: "Exporting...") */
  exportingLabel?: string;
}

const defaultMessage = 'Do you want to export the data to Excel?';
const defaultTitle = 'Export to Excel';

const ExportConfirmModal: React.FC<ExportConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isExporting,
  title = defaultTitle,
  message = defaultMessage,
  confirmLabel = 'Export',
  exportingLabel = 'Exporting...',
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      closeDisabled={isExporting}
      title={
        <>
          <FileSpreadsheet className="h-5 w-5 shrink-0" aria-hidden />
          {title}
        </>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <LoadingSpinner />
                <span>{exportingLabel}</span>
              </>
            ) : (
              <span>{confirmLabel}</span>
            )}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">{message}</p>
    </CommonModal>
  );
};

export default ExportConfirmModal;
